import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Search,
  UserX,
  Trash2,
  Eye,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { apiService } from '../../services/api';

interface PendingRegistration {
  id: string;
  name: string;
  companyName: string;
  email: string;
  phoneNumber?: string;
  registrationProfileId: string;
  registrationProfileName: string;
  verificationToken: string;
  expiresAt: string;
  isCompleted: boolean;
  completedAt?: string;
  customerId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  isAdminCreated: boolean;
  isExpired: boolean;
}

interface PaginatedRegistrations {
  items: PendingRegistration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface RegistrationStats {
  total: number;
  pending: number;
  expired: number;
  recentlyAdded: number;
}

export function PendingRegistrationsPage() {
  const [registrations, setRegistrations] = useState<PaginatedRegistrations>({
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [stats, setStats] = useState<RegistrationStats>({
    total: 0,
    pending: 0,
    expired: 0,
    recentlyAdded: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'expired'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRegistration, setSelectedRegistration] = useState<PendingRegistration | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadRegistrations();
    loadStats();
  }, [currentPage, searchTerm, statusFilter]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const data = await apiService.getPendingRegistrations(params);
      setRegistrations(data);
    } catch (error) {
      toast.error('Failed to load pending registrations');
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiService.getPendingRegistrationStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration || !rejectionReason.trim()) return;

    try {
      await apiService.rejectPendingRegistration(selectedRegistration.id, {
        reason: rejectionReason.trim(),
      });
      toast.success('Registration rejected successfully');
      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedRegistration(null);
      loadRegistrations();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject registration');
    }
  };

  const handleDelete = async () => {
    if (!selectedRegistration) return;

    try {
      await apiService.deletePendingRegistration(selectedRegistration.id);
      toast.success('Registration deleted successfully');
      setShowDeleteDialog(false);
      setSelectedRegistration(null);
      loadRegistrations();
      loadStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete registration');
    }
  };

  const getStatusBadge = (registration: PendingRegistration) => {
    return (
      <div className="flex items-center gap-2">
        {registration.isExpired ? (
          <Badge variant="destructive">Abgelaufen</Badge>
        ) : (
          <Badge variant="secondary">Ausstehend</Badge>
        )}
        {registration.isAdminCreated && (
          <Badge variant="outline" className="border-blue-600 text-blue-600">
            Admin
          </Badge>
        )}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pending Registrations</h1>
        <p className="text-muted-foreground">
          Manage and review new customer registration requests
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 24h</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.recentlyAdded}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Registration List */}
      <Card>
        <CardHeader>
          <CardTitle>Registration Requests</CardTitle>
          <CardDescription>
            Review and manage pending customer registrations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {registrations.items.map((registration) => (
                  <div
                    key={registration.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{registration.name}</p>
                          <p className="text-sm text-muted-foreground">{registration.email}</p>
                        </div>
                        {getStatusBadge(registration)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Company: {registration.companyName}</p>
                        <p>Profile: {registration.registrationProfileName}</p>
                        <p>Expires: {formatDate(registration.expiresAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRegistration(registration);
                          setShowDetailsDialog(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!registration.isExpired && (
                        <>
                          {/* HIDDEN: Approve button - Owner verifies via email link now */}
                          {/* <Button
                            variant="outline"
                            size="sm"
                            className="border-green-600 text-green-600 hover:bg-green-50 hover:text-green-700 hover:border-green-700"
                            onClick={() => {
                              setSelectedRegistration(registration);
                              setShowApproveDialog(true);
                            }}
                            title="Registrierung genehmigen"
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button> */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-700"
                            onClick={() => {
                              setSelectedRegistration(registration);
                              setShowRejectDialog(true);
                            }}
                            title="Registrierung ablehnen"
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          setSelectedRegistration(registration);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {registrations.totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, registrations.total)} of {registrations.total} results
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {registrations.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(registrations.totalPages, currentPage + 1))}
                      disabled={currentPage === registrations.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
            <DialogDescription>
              Complete information for this registration request
            </DialogDescription>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <p className="text-sm text-muted-foreground">{selectedRegistration.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Company</label>
                  <p className="text-sm text-muted-foreground">{selectedRegistration.companyName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <p className="text-sm text-muted-foreground">{selectedRegistration.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <p className="text-sm text-muted-foreground">{selectedRegistration.phoneNumber || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Profile</label>
                  <p className="text-sm text-muted-foreground">{selectedRegistration.registrationProfileName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedRegistration)}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Created</label>
                  <p className="text-sm text-muted-foreground">{formatDate(selectedRegistration.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Expires</label>
                  <p className="text-sm text-muted-foreground">{formatDate(selectedRegistration.expiresAt)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedRegistration && !selectedRegistration.isExpired && (
              <>
                {/* HIDDEN: Approve button - Owner verifies via email link now */}
                {/* <Button
                  variant="default"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setShowApproveDialog(true);
                  }}
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Genehmigen
                </Button> */}
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setShowRejectDialog(true);
                  }}
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Ablehnen
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HIDDEN: Approve Dialog - Owner verifies via email link now */}
      {/* <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Registration</DialogTitle>
            <DialogDescription>
              This will create a new customer account for {selectedRegistration?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start space-x-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-blue-900">
                  Automatische Passwort-Generierung
                </p>
                <p className="text-sm text-blue-700">
                  Ein sicheres, zufälliges Passwort wird generiert und per Email an {selectedRegistration?.email} gesendet.
                </p>
              </div>
            </div>
            {selectedRegistration && (
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Firma:</span> {selectedRegistration.companyName}</p>
                <p><span className="font-medium">Owner:</span> {selectedRegistration.name}</p>
                <p><span className="font-medium">Profil:</span> {selectedRegistration.registrationProfileName}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="default" onClick={handleApprove}>
              Registrierung genehmigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this registration request.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium">Rejection Reason</label>
            <Textarea
              placeholder="Enter the reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectionReason.trim()}
            >
              Reject Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Registration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this registration request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
